/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { ShowMoreGridSection } from '@coursera-web/cds-labs';

import { breakpoints, useMediaQuery } from '@coursera/cds-core';

import type { PartnerDegreeFragment } from 'bundles/partners-graphql/components/cds/queries/__generated__/PartnerDegreesQuery';
import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import { LEARNING_PRODUCTS } from 'bundles/search-common/constants';

import _t from 'i18n!nls/partners-graphql';

const styles = {
  sectionPadding: css`
    padding: var(--cds-spacing-300) 0;
  `,
};

type Props = {
  degrees: PartnerDegreeFragment[];
  title?: string;
};

const DegreeCardsSection = ({ degrees, title = _t('Degrees') }: Props) => {
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  if (degrees.length === 0) {
    return null;
  }

  const cardVariant = isMobile || degrees.length <= 2 ? 'list' : 'grid';

  const renderDegreeCard = ({ item: degree, itemIndex }: { item: PartnerDegreeFragment; itemIndex: number }) => {
    const degreeUrl = `/degrees/${degree.slug}`;

    return (
      <LearningProductCard
        id={degree.id}
        slug={degree.slug}
        name={degree.name}
        linkProps={{ href: degreeUrl }}
        variant={cardVariant}
        productType={
          degree.productVariant === 'UniversityCertificate'
            ? LEARNING_PRODUCTS.UniversityCertificate
            : LEARNING_PRODUCTS.Degree
        }
        partners={degree.partners.flatMap((partner) =>
          partner ? [{ id: partner.id, name: partner.name, logoUrl: partner.logo ?? undefined }] : []
        )}
        ImageComponent={
          degree.thumbnailUrl ? (
            <LearningProductCardImage imageUrl={degree.thumbnailUrl} variant={cardVariant} />
          ) : undefined
        }
        cardIndex={itemIndex}
      />
    );
  };

  return (
    <ShowMoreGridSection
      css={styles.sectionPadding}
      title={title}
      items={degrees}
      renderItem={renderDegreeCard}
      itemsGridConfig={cardVariant === 'list' ? { xs: 6, sm: 6, md: 6, lg: 6 } : { xs: 12, sm: 6, md: 4, lg: 3 }}
      defaultRows={isMobile ? 2 : 1}
      showMoreAmount={8}
      spacing={24}
    />
  );
};

export default DegreeCardsSection;
